import { RefObject, useState } from "react";
import { WebGLRenderer } from "three";
import Image from "next/image";
import styles from "@/styles/Canvas.module.scss";
import { motion, AnimatePresence } from "framer-motion";
import { CopyText } from "./CopyText";
import { ImageLoader } from "./ContentSections/ImageLoader";
import { Slide, toast } from "react-toastify";
import { PageType } from "@/types";

function ScreenshotButton({ glRef, page }: { glRef: RefObject<WebGLRenderer>; page: PageType }) {
	const [takeScreenshot, setTakeScreenshot] = useState<boolean>(false);
	const [waitForScreenshot, setWaitForScreenshot] = useState<boolean>(false);

	const [blobImage, setBlobImage] = useState<string | null>(null);
	const [shareFile, setShareFile] = useState<File | null>(null);

	const capture = async () => {
		// console.log("capture", glRef.current);

		if (!glRef.current) return;

		const oldDpr = glRef.current.getPixelRatio();
		glRef.current.setPixelRatio(1);

		await new Promise(requestAnimationFrame);

		setTakeScreenshot(true);
		setWaitForScreenshot(true);
		setTimeout(() => {
			setTakeScreenshot(false);
		}, 200);

		glRef.current.domElement.toBlob(async (blob) => {
			setBlobImage(null);
			setShareFile(null);
			setWaitForScreenshot(false);

			glRef.current?.setPixelRatio(oldDpr);

			if (!blob) return;

			setBlobImage(URL.createObjectURL(blob));

			// In Datei umwandeln, um mit der Share API teilen zu können
			const file = new File([blob], "wissenslabor3d_foto.png", { type: "image/png" });

			setShareFile(file);
		}, "image/png");
	};

	const share = async () => {
		if (!shareFile) {
			setBlobImage(null);
			setShareFile(null);
			return;
		}
		// Prüfen, ob Web Share API unterstützt wird
		if (navigator.canShare && navigator.canShare({ files: [shareFile] })) {
			try {
				await navigator.share({
					files: [shareFile],
					title: "Wissenslabor3d",
					text: page.sharetext,
				});
				// console.log("Bild erfolgreich geteilt!");
			} catch (error) {
				console.error("Fehler beim Teilen:", error);

				toast.error("Ups! Das hat leider nicht funktioniert.", {
					position: "top-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: false,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					closeButton: false,
					theme: "dark",
					transition: Slide,
				});
				// Reset
				setBlobImage(null);
				setShareFile(null);
			}
		} else {
			console.error("Web Share API wird nicht unterstützt.");

			toast.error("Ups! Das hat leider nicht funktioniert.", {
				position: "top-center",
				autoClose: 5000,
				hideProgressBar: true,
				closeOnClick: false,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				closeButton: false,
				theme: "dark",
				transition: Slide,
			});
			// Reset
			setBlobImage(null);
			setShareFile(null);
		}
	};

	const cancel = () => {
		setBlobImage(null);
		setShareFile(null);
	};

	return (
		<>
			<AnimatePresence>
				{(waitForScreenshot || blobImage) && (
					<motion.div
						className={styles.fullscreenBlob}
						initial={{ opacity: 0 }}
						animate={waitForScreenshot || blobImage ? { opacity: 1 } : { opacity: 0 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.1 }}
					>
						<div className={styles.imageHolder}>
							<div className={styles.imageHolderInner}>
								<div className={styles.blobImageAppear}>
									{!blobImage && <ImageLoader />}
									{blobImage && <Image className={styles.blobImageAppearImg} src={blobImage} alt="Capture" width={0} height={0} />}
								</div>
								<div className={styles.extraInfo}>
									<p>Teile dieses Foto auf Instagram oder schicke es Deinen Freund*innen!</p>
									<CopyText text={page.sharetext} />
								</div>
							</div>
						</div>
						<div className={styles.shareNavigation}>
							<button disabled={waitForScreenshot} onClick={cancel}>
								zurück
							</button>
							<button disabled={waitForScreenshot} onClick={share}>
								Teilen
							</button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<AnimatePresence>
				{takeScreenshot && <motion.div className={styles.screenshotOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}></motion.div>}
			</AnimatePresence>

			<button title="Bild aufnehmen" className={styles.screenshot} onClick={capture}></button>
		</>
	);
}

export default ScreenshotButton;
